import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "caire-cookie-consent";
const ADS_ID = "AW-18085351326";

/**
 * Consent gate for the Google Ads tag. The tag is injected only after an
 * explicit opt-in, because CAIRE B.V. is EU-established and advertising
 * cookies need prior consent under the Dutch Telecommunicatiewet art. 11.7a.
 *
 * ponytail: consent is stored in localStorage, so it is per-browser rather
 * than per-user and resets when site data is cleared. That is fine for a
 * brochure site with one non-essential tag; the upgrade path (a real CMP with
 * an audit trail and per-vendor granularity) only becomes necessary if more
 * third-party tags are added.
 */
function loadAdsTag() {
  if (document.getElementById("gtag-src")) return;

  const script = document.createElement("script");
  script.id = "gtag-src";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  gtag("js", new Date());
  gtag("config", ADS_ID);
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "accepted") loadAdsTag();
    else if (stored !== "rejected") setVisible(true);
  }, []);

  const decide = (choice: "accepted" | "rejected") => {
    localStorage.setItem(STORAGE_KEY, choice);
    setVisible(false);
    if (choice === "accepted") loadAdsTag();
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-background/95 backdrop-blur-sm px-4 py-4 md:px-6"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          We use one optional cookie to measure how our advertising performs. It is off unless you
          allow it, and the site works exactly the same either way. See our{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex flex-shrink-0 items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => decide("rejected")}>
            Decline
          </Button>
          <Button size="sm" onClick={() => decide("accepted")}>
            Allow
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Clears the stored choice so the banner reappears, giving people a way to withdraw consent. */
export function resetCookieConsent() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}
